import { decodeHTML } from "entities";
import type {
  ProductCardD2CClientProps,
  VariantCardData,
} from "@/scott/ui/blocks/product-card-d2c/product-card-d2c.client";
import type {
  ActiveProductFilterD2C,
  ProductFilterGroupD2C,
  ProductSortOptionD2C,
} from "@/scott/ui/blocks/product-listing-d2c/product-listing-controls-d2c";
import type {
  ProductListingD2CProps,
  ProductListingD2CVariant,
  ProductListingPaginationModeD2C,
} from "@/scott/ui/blocks/product-listing-d2c/product-listing-d2c";
import {
  type BadgeTexts,
  buildBadges,
} from "@/scott/ui/elements/badge/badge.utils";
import colorNameMap from "@/scott/ui/elements/color-swatch/color-name-map.json" with {
  type: "json",
};
import { buildColorSwatches } from "@/scott/ui/elements/color-swatch/color-swatch.utils";
import type { ProductDetailVariant } from "@/services/product/get-product-detail";
import type {
  ProductListingData,
  ProductListingFacet,
  ProductListingProduct,
} from "@/services/product/get-product-listing";
import type { UiConfiguration } from "@/types/page-block";
import { getProductDetailStockStatus } from "@/ui/pages/product-detail-page/product-detail-page.stock";
import { formatPrice, getPriceLocale } from "@/utils/price-format";
import { buildD2CPdpUrlForProduct } from "@/utils/product-detail-page/pdp-url-builder";

type CardMicrocopies = {
  configure?: string;
  add?: string;
  compare?: string;
  badges: BadgeTexts;
};

export type ProductListingMicrocopies =
  ProductListingD2CProps["microcopies"] & {
    cardConfigure?: string;
    cardAdd?: string;
    cardCompare?: string;
    cardBadges?: BadgeTexts;
    sortRelevant: string;
    sortPriceAscending: string;
    sortPriceDescending: string;
  };

export type ProductListingPresentation = {
  variant?: ProductListingD2CVariant;
  showCarouselNavigationArrows?: boolean;
  showFilters?: boolean;
  showSorting?: boolean;
  baseQuery?: string;
  paginationMode?: ProductListingPaginationModeD2C;
};

function normalizeSaleFields(
  product: ProductListingProduct
): ProductListingProduct {
  return {
    ...product,
    discountString: product.discountString?.trim() || undefined,
    variantProducts: product.variantProducts?.map((variant) => ({
      ...variant,
      discountString: variant.discountString?.trim() || undefined,
    })),
  };
}

const DESCRIPTION_MAX_LENGTH = 200;
// Matches the grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 layout in product-listing-d2c.tsx
const CARD_IMAGE_SIZES =
  "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";
const CLOUDINARY_TRANSFORMS = [
  "w_768",
  "ar_4:3",
  "c_lpad",
  "b_transparent",
  "g_center",
  "q_auto",
];

function withCloudinaryTransform(url: string, transform: string): string {
  // Cloudinary is delivered from both res.cloudinary.com (test) and the custom
  // delivery CNAME static.scott-sports.com (prod). Match the delivery path
  // (/image/upload/) instead of a single host so both environments are rewritten
  // — otherwise prod URLs pass through untouched and keep the backend's small
  // t_assetformatoverview thumbnail, which renders pixelated at card size.
  if (!url.includes("/image/upload/")) {
    return url;
  }

  // Replace an existing named transform (e.g. t_assetformatoverview) with the
  // card transform; otherwise insert it right after /upload/.
  return url.includes("/t_")
    ? url.replace(/\/t_[^/]+/, `/${transform}`)
    : url.replace("/upload/", `/upload/${transform}/`);
}

function withOverviewImage<T extends { imageUrl?: string; mainAsset?: string }>(
  variant: T
): T {
  const src = variant.mainAsset || variant.imageUrl;
  return src
    ? {
        ...variant,
        imageUrl: withCloudinaryTransform(src, CLOUDINARY_TRANSFORMS.join(",")),
      }
    : variant;
}

function normalizeDescriptionText(html: string): string {
  let decoded = html.replace(/<[^>]+>/g, " ");

  // A second pass handles occasionally double-encoded values from upstream APIs.
  for (const _ of [0, 1]) {
    const next = decodeHTML(decoded);
    if (next === decoded) {
      break;
    }
    decoded = next;
  }

  return decoded
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function cropDescription(html: string, maxLength: number): string {
  const plain = normalizeDescriptionText(html);
  if (plain.length <= maxLength) {
    return plain;
  }

  const truncated = plain.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  return lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated;
}

function getCardPrice(
  product: ProductListingProduct,
  locale: string | undefined
): ProductCardD2CClientProps["price"] {
  // Without a route locale, SAP's own formatting is the best guess.
  const format = (price: Parameters<typeof formatPrice>[0]) =>
    locale ? formatPrice(price, locale) : price?.formattedValue;

  const activePrice = product.price;
  // SAP is migrating the post-promotion consumer price to its own
  // `consumerDiscountedPrice` field; base stores that do not serve it yet still
  // return the discounted value as `consumerPrice`.
  const consumerPrice =
    product.consumerDiscountedPrice ?? product.consumerPrice;
  const activePriceValue = activePrice?.value;
  const consumerPriceValue = consumerPrice?.value;

  // Mirrors the PDP: any gap between the two prices is a discount, whether or
  // not SAP also sent a `discountString`.
  if (
    typeof activePriceValue === "number" &&
    typeof consumerPriceValue === "number" &&
    activePriceValue !== consumerPriceValue
  ) {
    const [discountedPrice, regularPrice] =
      activePriceValue < consumerPriceValue
        ? [activePrice, consumerPrice]
        : [consumerPrice, activePrice];

    const price = format(discountedPrice);
    const strikePrice = format(regularPrice);
    if (price && strikePrice) {
      return { price, strikePrice };
    }
  }

  return {
    price: format(consumerPrice) ?? format(activePrice) ?? "",
  };
}

type SaleFields = Pick<
  ProductListingProduct,
  "price" | "consumerPrice" | "consumerDiscountedPrice" | "discountString"
>;

function getVariantSaleFields(
  variant: ProductDetailVariant,
  parent: ProductListingProduct
): SaleFields {
  // Price fields are merged from the listing API into each variant by mergeSaleFields.
  // Cast is safe: variant price structure mirrors ProductListingProduct after merge.
  const v = variant as SaleFields;

  // A promotion can cover a single colour, and the base product may carry that
  // colour's discount. A variant with its own price must not inherit it.
  if (v.price || v.consumerPrice || v.consumerDiscountedPrice) {
    return {
      price: v.price ?? parent.price,
      consumerPrice: v.consumerPrice,
      consumerDiscountedPrice: v.consumerDiscountedPrice,
      discountString: v.discountString,
    };
  }

  return {
    price: parent.price,
    consumerPrice: parent.consumerPrice,
    consumerDiscountedPrice: parent.consumerDiscountedPrice,
    discountString: v.discountString ?? parent.discountString,
  };
}

/**
 * Draws the colour a card opens on when none was picked. The draw is over
 * colours, not articles, so a colour with many sizes is no likelier than one
 * with few.
 */
function pickRandomColourVariant(
  variants: ProductDetailVariant[]
): ProductDetailVariant | undefined {
  const firstArticleOfColour = new Map<string, ProductDetailVariant>();
  for (const variant of variants) {
    const key = variant.color?.name ?? variant.code ?? "";
    if (!firstArticleOfColour.has(key)) {
      firstArticleOfColour.set(key, variant);
    }
  }

  const candidates = [...firstArticleOfColour.values()];
  return candidates[Math.floor(Math.random() * candidates.length)]; // NOSONAR: only picks which colour a card opens on, nothing security related
}

function mapProductToCardProps(
  product: ProductListingProduct,
  microcopies: CardMicrocopies,
  routeContext: { country?: string; language?: string }
): ProductCardD2CClientProps {
  const normalizedProduct = normalizeSaleFields(product);
  const variants = normalizedProduct.variantProducts ?? [];

  // Priority: server-resolved variant (product.code set to the picked article in
  // specific-products mode) → random colour → first.
  const resolvedVariant = variants.find((v) => v.code === product.code);
  const activeVariant: Partial<ProductDetailVariant> =
    resolvedVariant ?? pickRandomColourVariant(variants) ?? {};

  const baseHref =
    routeContext.country && routeContext.language
      ? buildD2CPdpUrlForProduct({
          country: routeContext.country,
          language: routeContext.language,
          product,
        })
      : undefined;
  const href =
    routeContext.country && routeContext.language
      ? buildD2CPdpUrlForProduct({
          country: routeContext.country,
          language: routeContext.language,
          product,
          articleCode: product.code,
        })
      : undefined;

  const colorSwatches = buildColorSwatches(
    variants.map(withOverviewImage),
    colorNameMap,
    activeVariant.color?.name,
    baseHref ?? product.url
  );

  const itemName = activeVariant.name ?? product.name ?? "";
  const priceLocale = routeContext.language
    ? getPriceLocale(routeContext.language, routeContext.country)
    : undefined;
  const priceSource = product.consumerPrice ?? product.price;
  const stockVariant = resolvedVariant ?? variants[0];

  // Build per-variant card data so the client updates ALL fields on swatch click.
  const variantData: Record<string, VariantCardData> = {};
  for (const swatch of colorSwatches.swatches) {
    const variant = variants.find((v) => v.color?.name === swatch.label);
    if (!variant) {
      continue;
    }
    const variantSaleFields = getVariantSaleFields(variant, normalizedProduct);
    variantData[swatch.id] = {
      image: { src: swatch.imageUrl ?? "" },
      name: variant.name ?? product.name ?? "",
      href: swatch.href,
      price: getCardPrice(
        { ...normalizedProduct, ...variantSaleFields },
        priceLocale
      ),
      badges: buildBadges(
        { ...normalizedProduct, ...variantSaleFields },
        [variant],
        microcopies.badges
      ),
      productCode: variant.code ?? "",
    };
  }

  return {
    image: {
      src: withCloudinaryTransform(
        activeVariant.mainAsset ||
          activeVariant.imageUrl ||
          // A product entered by its article code is the variant itself: SAP
          // nests no variantProducts under it and its image sits on the product.
          product.mainAsset ||
          product.imageUrl ||
          `https://placehold.co/600x450/f5f5f5/333.png?text=${encodeURIComponent(itemName)}`,
        CLOUDINARY_TRANSFORMS.join(",")
      ),
      alt: itemName,
      aspectRatio: "4:3" as const,
      sizes: CARD_IMAGE_SIZES,
    },
    price: getCardPrice(normalizedProduct, priceLocale),
    href: href || product.url || undefined,
    badges: buildBadges(normalizedProduct, variants, microcopies.badges),
    colorSwatches: colorSwatches.swatches.length ? colorSwatches : undefined,
    variantData: Object.keys(variantData).length > 0 ? variantData : undefined,
    microcopies: {
      name: itemName,
      productCode: product.code ?? "",
      description: product.description
        ? `${cropDescription(product.description, DESCRIPTION_MAX_LENGTH)} ...`
        : undefined,
      configure: microcopies.configure,
      add: microcopies.add,
      compare: microcopies.compare,
    },
    gtm: {
      variantCode: activeVariant.code,
      brand: product.brand,
      categories: product.categories?.map((c) => ({
        code: c.code ?? "",
        name: c.name ?? undefined,
      })),
      colorName: activeVariant.color?.name,
      currency: priceSource?.currencyIso,
      priceValue: priceSource?.value,
      stockStatus: stockVariant
        ? getProductDetailStockStatus(stockVariant)
        : undefined,
      itemName,
    },
  };
}

function getOptionId(groupCode: string, query: string, index: number): string {
  return `${groupCode}-${index}-${query.length}`;
}

function splitFacetLabel(value: string): {
  title: string;
  optionLabel?: string;
} {
  const [title, optionLabel] = value.split("||", 2).map((part) => part.trim());

  return {
    title: title || value,
    optionLabel: optionLabel || undefined,
  };
}

function decodeFacetQuery(value: string | undefined): string {
  if (!value) {
    return "";
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

const AVAILABILITY_STORE_PATTERN = /:available:([^:]+)/g;

/**
 * Counts the distinct base stores referenced by a facet's value queries. The
 * availability facet lists one value per base store (`…:available:<store>`), so
 * it resolves to several stores. Every other facet only ever carries the single
 * active availability segment (when availability is applied), so it resolves to
 * at most one store — which is how we tell them apart. SAP does not return facet
 * `code` under `fields=DEFAULT,facets`, so detection must rely on the queries.
 */
function countAvailabilityStores(
  values: NonNullable<ProductListingFacet["values"]>
): number {
  const stores = new Set<string>();
  for (const value of values) {
    const query = decodeFacetQuery(value.query?.query?.value);
    for (const match of query.matchAll(AVAILABILITY_STORE_PATTERN)) {
      if (match[1]) {
        stores.add(match[1]);
      }
    }
  }
  return stores.size;
}

function filterAvailabilityValues(
  values: NonNullable<ProductListingFacet["values"]>,
  baseSiteId: string
): NonNullable<ProductListingFacet["values"]> {
  const baseStoreId = baseSiteId.replaceAll("_", "-");
  // Keep only the current base store's value. When the filter is NOT active the
  // value carries `:available:<baseStore>`; once it IS active the backend returns
  // it with a toggle-off query (e.g. ":relevant") that drops that segment and sets
  // `selected: true` instead. Matching on either signal keeps the group visible
  // whether or not the filter is applied. SAP does not return value `code` under
  // `fields=DEFAULT,facets`, so we cannot rely on it.
  return values.filter(
    (value) =>
      value.selected === true ||
      decodeFacetQuery(value.query?.query?.value).includes(
        `:available:${baseStoreId}`
      )
  );
}

function normalizeFilterGroups(
  apiData: ProductListingData,
  baseSiteId: string
): ProductFilterGroupD2C[] {
  const groups = apiData.facets.flatMap((facet, facetIndex) => {
    if (facet.visible === false || !facet.name) {
      return [];
    }

    let values = facet.values?.length ? facet.values : (facet.topValues ?? []);

    const isAvailability = countAvailabilityStores(values) >= 2;

    if (isAvailability) {
      values = filterAvailabilityValues(values, baseSiteId);
      if (values.length === 0) {
        return [];
      }
    }

    const { title, optionLabel } = splitFacetLabel(facet.name);
    const code = `${title}-${facetIndex}`;
    const selection: "multiple" | "single" =
      facet.multiSelect === true ? "multiple" : "single";
    const options = values.flatMap((value, valueIndex) => {
      const query = value.query?.query?.value;

      if (!(value.name && query !== undefined)) {
        return [];
      }

      const valueLabel = optionLabel ?? splitFacetLabel(value.name).optionLabel;
      const label = valueLabel ?? value.name;

      return [
        {
          id: getOptionId(code, query, valueIndex),
          label,
          count: value.count,
          query,
          selected: value.selected === true,
        },
      ];
    });

    return [
      {
        type: "options" as const,
        code,
        title,
        selection,
        display: "list" as const,
        options,
      },
    ];
  });

  return groups.map((group, index) => ({
    ...group,
    defaultOpen: index === 0,
  }));
}

function normalizeActiveFilters(
  apiData: ProductListingData,
  groups: ProductFilterGroupD2C[]
): ActiveProductFilterD2C[] {
  const breadcrumbs = apiData.breadcrumbs.flatMap((breadcrumb, index) => {
    const query = breadcrumb.removeQuery?.query?.value;
    const rawLabel = breadcrumb.facetValueName;
    const label = rawLabel
      ? (splitFacetLabel(rawLabel).optionLabel ?? rawLabel)
      : undefined;

    if (!(query !== undefined && label)) {
      return [];
    }

    return [
      {
        id: `${breadcrumb.facetCode ?? breadcrumb.facetName ?? "filter"}-${breadcrumb.facetValueCode ?? index}`,
        groupCode: breadcrumb.facetCode ?? breadcrumb.facetName ?? "filter",
        label,
        query,
      },
    ];
  });

  if (breadcrumbs.length > 0) {
    return breadcrumbs;
  }

  return groups.flatMap((group) => {
    if (group.type !== "options") {
      return [];
    }

    return group.options.flatMap((option) =>
      option.selected
        ? [
            {
              id: option.id,
              groupCode: group.code,
              label: option.label,
              query: option.query,
            },
          ]
        : []
    );
  });
}

function humanizeSortCode(code: string): string {
  return code
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getSortLabel(
  code: string,
  apiName: string | undefined,
  microcopies: ProductListingMicrocopies
): string {
  const labels: Record<string, string> = {
    relevant: microcopies.sortRelevant,
    relevance: microcopies.sortRelevant,
    "price-asc": microcopies.sortPriceAscending,
    "price-desc": microcopies.sortPriceDescending,
  };

  return labels[code] ?? apiName ?? humanizeSortCode(code);
}

function normalizeSortOptions(
  apiData: ProductListingData,
  microcopies: ProductListingMicrocopies
): ProductSortOptionD2C[] {
  return apiData.sorts.flatMap((sort) => {
    if (!sort.code || sort.code === "random") {
      return [];
    }

    return [
      {
        code: sort.code,
        label: getSortLabel(sort.code, sort.name, microcopies),
        selected:
          sort.selected === true || apiData.pagination?.sort === sort.code,
      },
    ];
  });
}

/**
 * Transforms product listing API data into UI-ready props
 */
export function productListingServerAdapter(
  apiData: ProductListingData,
  uiConfig: UiConfiguration,
  microcopies: ProductListingMicrocopies,
  presentation: ProductListingPresentation,
  title?: string | undefined,
  sysId?: string,
  anchorId?: string,
  routeContext: { country?: string; language?: string } = {},
  baseSiteId = ""
): ProductListingD2CProps {
  const cardMicrocopies: CardMicrocopies = {
    configure: microcopies.cardConfigure,
    add: microcopies.cardAdd,
    compare: microcopies.cardCompare,
    badges: microcopies.cardBadges ?? {},
  };

  const products = apiData.products.map((product) =>
    mapProductToCardProps(product, cardMicrocopies, routeContext)
  );
  const filterGroups = normalizeFilterGroups(apiData, baseSiteId);
  const sortOptions = normalizeSortOptions(apiData, microcopies);

  return {
    sysId,
    title,
    anchorId,
    variant: presentation.variant ?? "grid",
    showCarouselNavigationArrows: presentation.showCarouselNavigationArrows,
    fullWidth: true,
    products,
    // Stays undefined when the count fetch failed on a cold cache; the UI then
    // shows no total instead of a wrong (0).
    count: apiData.count,
    showFilters: presentation.showFilters ?? true,
    showSort: presentation.showSorting ?? true,
    filterGroups,
    activeFilters: normalizeActiveFilters(apiData, filterGroups),
    sortOptions,
    selectedSort:
      sortOptions.find((option) => option.selected)?.code ??
      apiData.pagination?.sort,
    paginationMode: presentation.paginationMode ?? "paginate",
    pagination: apiData.pagination
      ? {
          currentPage: apiData.pagination.currentPage ?? 0,
          totalPages: apiData.pagination.totalPages ?? 0,
        }
      : undefined,
    microcopies,
    backgroundColor: uiConfig.backgroundColor ?? "whitesmoke",
    paddingTop: uiConfig.paddingTop,
    paddingBottom: uiConfig.paddingBottom,
  };
}
